import { FieldTemplateProps } from '@rjsf/utils/src/types';
import { Container, FormField } from '@amzn/awsui-components-react-v3';
import * as React from 'react';

/**
 * Custom field template that conditionally renders fields based on content.
 *
 * Features:
 * - Hides fields marked as hidden
 * - Wraps object fields in Container for visual grouping
 * - Uses simple FormField wrapper for primitive fields
 *
 * @param props - Standard RJSF field template props
 * @returns JSX element or empty fragment for hidden fields
 */
export const CompactFieldTemplate = (props: FieldTemplateProps) => {
  const { formData, label, description, schema } = props;

  // Hide if field is marked as hidden
  if (formData === HIDDEN) {
    return <></>;
  }

  // Hide array fields with no data when using a custom field (ui:field)
  if (props.uiSchema?.['ui:field'] && Array.isArray(formData) && formData.length === 0) {
    return <></>;
  }

  // Array fields with uniqueItems use a custom widget (e.g. GroupedMultiselectWidget).
  // Render them like primitive fields — just a FormField with label — to avoid the
  // extra Container wrapper that the object branch would add.
  if (schema.type === 'array' && schema.uniqueItems) {
    return (
      <FormField label={label} description={description}>
        {props.children}
      </FormField>
    );
  }

  // Boolean fields in the form view use CheckboxOnlyAfterClickLinkWidget which renders
  // its own label, so skip the outer label to avoid duplication.
  // In the diff view, formData is a diff-encoded string (not a boolean), so we keep the label.
  if (schema.type === 'boolean' && typeof formData !== 'string') {
    return <>{props.children}</>;
  }

  return typeof formData === 'object' ? (
    <Container>
      <FormField label={label} description={description}>
        {props.children}
      </FormField>
    </Container>
  ) : (
    <FormField label={label} description={description}>
      {props.children}
    </FormField>
  );
};

// cannot use Symbol because json schema form doesn't work with it
export const HIDDEN = '\u0000';
